import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, PenLine, Upload, XCircle } from 'lucide-react';
import { api, download, getAccessToken } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { useProtectedImage } from './company-logo';
import { Alert, Badge, Button, Card, DataTable, EmptyState, ErrorBox, Field, Modal, Spinner, Textarea, Toggle, useToast } from './ui';

/** Panel para firmar con el dedo o el ratón. Devuelve la firma como PNG (data URL), o null si está vacío. */
export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const empty = useRef(true);
  useEffect(() => {
    const c = ref.current!;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext('2d')!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e3a8a';
  }, []);
  const pos = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    empty.current = true;
    onChange(null);
  };
  return (
    <div>
      <canvas
        ref={ref}
        aria-label="Firma: dibuja con el dedo o el ratón"
        className="h-36 w-full touch-none rounded-xl border-2 border-dashed border-slate-300 bg-white"
        onPointerDown={(e) => {
          drawing.current = true;
          ref.current!.setPointerCapture(e.pointerId);
          const p = pos(e);
          const ctx = ref.current!.getContext('2d')!;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const p = pos(e);
          const ctx = ref.current!.getContext('2d')!;
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          empty.current = false;
        }}
        onPointerUp={() => {
          drawing.current = false;
          if (!empty.current) onChange(ref.current!.toDataURL('image/png'));
        }}
      />
      <div className="mt-1 flex justify-between text-xs text-slate-500">
        <span>Dibuja tu firma (opcional)</span>
        <button type="button" className="text-brand-700 hover:underline" onClick={clear}>
          Borrar
        </button>
      </div>
    </div>
  );
}

/** El trabajador firma su registro mensual: conforme o no conforme (con motivo), con su firma dibujada si quiere. */
export function SignRecordModal({ doc, onClose }: { doc: { id: string; title: string } | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [decision, setDecision] = useState<'AGREE' | 'DISAGREE'>('AGREE');
  const [comment, setComment] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const sign = useMutation({
    mutationFn: () => api.post(`/api/v1/documents/${doc!.id}/sign`, { as: 'EMPLOYEE', decision, comment: comment || undefined, signatureImage: image ?? undefined }),
    onSuccess: () => {
      toast.success(decision === 'AGREE' ? 'Registro firmado.' : 'Has indicado que no estás conforme. Tu responsable lo revisará.');
      qc.invalidateQueries({ queryKey: ['me', 'documents'] });
      onClose();
    },
  });
  return (
    <Modal
      open={Boolean(doc)}
      onClose={onClose}
      title="Firmar mi registro horario"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant={decision === 'AGREE' ? 'success' : 'danger'} loading={sign.isPending} disabled={decision === 'DISAGREE' && comment.trim().length < 5} onClick={() => sign.mutate()}>
            {decision === 'AGREE' ? 'Firmar conforme' : 'Enviar disconformidad'}
          </Button>
        </>
      }
    >
      {doc && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            <strong>{doc.title}</strong>. Revísalo antes de firmar:{' '}
            <button type="button" className="text-brand-700 underline" onClick={() => download(`/api/v1/documents/${doc.id}/download`).catch(toast.error)}>
              descargar el registro
            </button>
            .
          </p>
          <ErrorBox error={sign.error} />
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="¿Estás conforme?">
            {(['AGREE', 'DISAGREE'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={decision === v}
                onClick={() => setDecision(v)}
                className={`flex items-center gap-2 rounded-xl border p-3 text-left text-sm ${decision === v ? (v === 'AGREE' ? 'border-emerald-500 bg-emerald-50' : 'border-red-400 bg-red-50') : 'border-slate-200'}`}
              >
                {v === 'AGREE' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                {v === 'AGREE' ? 'Estoy conforme con mis horas' : 'No estoy conforme'}
              </button>
            ))}
          </div>
          {decision === 'DISAGREE' ? (
            <Field label="¿Qué no es correcto?" required hint="Tu responsable lo verá. Si hay que corregir algo, se corrige la jornada y se publica un registro nuevo.">
              {(id) => <Textarea id={id} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ej.: el día 10 salí a las 17:00, no a las 16:00" />}
            </Field>
          ) : (
            <Field label="Comentario (opcional)">{(id) => <Textarea id={id} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />}</Field>
          )}
          <SignaturePad onChange={setImage} />
          <p className="text-xs text-slate-500">Tu firma queda registrada con la fecha, la hora y la huella del documento, y no se puede modificar después.</p>
        </div>
      )}
    </Modal>
  );
}

/** Estado de firma de un registro en la lista del trabajador. */
export function SignatureBadges({ d }: { d: { mySignature: { decision: string } | null; companySignature: unknown } }) {
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {d.mySignature ? <Badge color={d.mySignature.decision === 'AGREE' ? 'green' : 'red'}>{d.mySignature.decision === 'AGREE' ? 'Firmado por ti' : 'No conforme'}</Badge> : <Badge color="amber">Pendiente de tu firma</Badge>}
      {d.companySignature ? <Badge color="blue">Firmado por la empresa</Badge> : null}
    </span>
  );
}

/** Panel de la empresa: firmas del registro mensual de cada trabajador, firma en bloque, recordatorio y sello. */
export function MonthlySignaturesCard({ period }: { period: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['monthly-signatures', period], queryFn: () => api.get<any>(`/api/v1/monthly-signatures/${period}`) });
  const bulk = useMutation({ mutationFn: () => api.post<{ signed: number }>(`/api/v1/monthly-signatures/${period}/sign-company`, {}), onSuccess: (r) => (toast.success(`${r.signed} registro(s) firmados por la empresa.`), qc.invalidateQueries({ queryKey: ['monthly-signatures'] })), onError: toast.error });
  const one = useMutation({ mutationFn: (id: string) => api.post(`/api/v1/documents/${id}/sign`, { as: 'COMPANY', decision: 'AGREE' }), onSuccess: () => (toast.success('Firmado por la empresa.'), qc.invalidateQueries({ queryKey: ['monthly-signatures'] })), onError: toast.error });
  const remind = useMutation({ mutationFn: () => api.post<{ reminded: number }>(`/api/v1/monthly-signatures/${period}/remind`, {}), onSuccess: (r) => toast.success(`Recordatorio enviado a ${r.reminded} trabajador(es).`), onError: toast.error });
  const toggle = useMutation({ mutationFn: (enabled: boolean) => api.patch('/api/v1/monthly-signatures/settings', { enabled }), onSuccess: () => qc.invalidateQueries({ queryKey: ['monthly-signatures'] }), onError: toast.error });
  const rows: any[] = data?.rows ?? [];
  const ready = rows.filter((r) => r.employee?.decision === 'AGREE' && !r.company).length;
  const pending = rows.filter((r) => !r.employee).length;
  return (
    <Card
      title={<span className="flex items-center gap-2"><PenLine className="h-4 w-4" /> Firmas del registro mensual</span>}
      description="Cada trabajador firma su registro del mes desde su app (conforme o no conforme) y después lo firma la empresa. El PDF firmado añade una hoja de firmas al registro original sin modificarlo."
      actions={
        data?.canSign && data.enabled ? (
          <div className="flex flex-wrap gap-2">
            {pending > 0 && (
              <Button size="sm" variant="secondary" loading={remind.isPending} onClick={() => remind.mutate()}>
                Recordar a {pending}
              </Button>
            )}
            <Button size="sm" disabled={!ready} loading={bulk.isPending} onClick={() => bulk.mutate()}>
              Firmar {ready || ''} conforme{ready === 1 ? '' : 's'}
            </Button>
          </div>
        ) : undefined
      }
      padded={false}
    >
      {data?.canSign && (
        <div className="border-b border-slate-100 px-5 py-3">
          <Toggle checked={Boolean(data.enabled)} onChange={(v) => toggle.mutate(v)} label="Los trabajadores firman su registro mensual" description="La ley no obliga a firmarlo. Si lo desactivas, el registro se sigue publicando a cada trabajador, pero sin firma." />
        </div>
      )}
      {data && !data.enabled ? (
        <EmptyState title="La firma del registro mensual está desactivada" />
      ) : (
        <>
      {data?.canSign && <StampBox />}
      {isLoading ? (
        <Spinner />
      ) : !rows.length ? (
        <EmptyState title="No hay registros publicados este mes" description="Se publican solos a primeros de mes, o desde «Informes mensuales individuales»." />
      ) : (
        <DataTable
          rowKey={(r) => r.documentId}
          rows={rows}
          columns={[
            { key: 'n', header: 'Trabajador', cell: (r) => <div><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">{r.code}</p></div> },
            {
              key: 'e',
              header: 'Trabajador/a',
              cell: (r) =>
                !r.employee ? (
                  <Badge color="amber">Pendiente</Badge>
                ) : r.employee.decision === 'AGREE' ? (
                  <span className="text-xs">
                    <Badge color="green">Conforme</Badge> <span className="text-slate-500">{fmtDateTime(r.employee.signedAt)}</span>
                  </span>
                ) : (
                  <span className="text-xs">
                    <Badge color="red">No conforme</Badge> <span className="text-slate-600">{r.employee.comment}</span>
                  </span>
                ),
            },
            {
              key: 'c',
              header: 'Empresa',
              cell: (r) =>
                r.company ? (
                  <Badge color="blue">Firmado</Badge>
                ) : data?.canSign && r.employee?.decision === 'AGREE' ? (
                  <Button size="sm" variant="ghost" loading={one.isPending && one.variables === r.documentId} onClick={() => one.mutate(r.documentId)}>
                    Firmar
                  </Button>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                ),
            },
            {
              key: 'd',
              header: '',
              cell: (r) =>
                r.employee || r.company ? (
                  <Button size="sm" variant="ghost" icon={<Download className="h-4 w-4" />} onClick={() => download(`/api/v1/documents/${r.documentId}/signed`).catch(toast.error)}>
                    PDF firmado
                  </Button>
                ) : null,
            },
          ]}
        />
      )}
        </>
      )}
    </Card>
  );
}

/** Imagen del sello o firma de la empresa (se estampa al firmar como empresa). */
function StampBox() {
  const toast = useToast();
  const [version, setVersion] = useState<string | null>('initial');
  const src = useProtectedImage('/api/v1/company/stamp', version, () => ({ authorization: `Bearer ${getAccessToken() ?? ''}` }));
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post<{ stampVersion: string }>('/api/v1/company/stamp', fd);
    },
    onSuccess: (r) => (setVersion(r.stampVersion), toast.success('Sello guardado. Se usará en las próximas firmas de la empresa.')),
    onError: toast.error,
  });
  const remove = useMutation({ mutationFn: () => api.del('/api/v1/company/stamp'), onSuccess: () => setVersion(null), onError: toast.error });
  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-slate-100 px-5 py-3">
      <div className="flex h-16 w-28 items-center justify-center rounded-lg border border-slate-200 bg-white">
        {src ? <img src={src} alt="Sello de la empresa" className="max-h-14 max-w-24 object-contain" /> : <span className="text-xs text-slate-400">Sin sello</span>}
      </div>
      <div className="flex-1 text-sm text-slate-600">
        <p className="font-medium text-slate-800">Sello o firma de la empresa</p>
        <p className="text-xs">Una foto o escaneo (PNG o JPG, mejor con fondo blanco o transparente). Aparece en la hoja de firmas cuando la empresa firma.</p>
      </div>
      <label className="cursor-pointer">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium hover:bg-slate-50">
          <Upload className="h-4 w-4" /> {src ? 'Cambiar' : 'Subir sello'}
        </span>
        <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} />
      </label>
      {src && (
        <Button size="sm" variant="ghost" onClick={() => remove.mutate()}>
          Quitar
        </Button>
      )}
      {upload.isPending && <Alert tone="info">Subiendo…</Alert>}
    </div>
  );
}
